import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Activity,
  Briefcase,
  Calendar,
  CheckCircle2,
  Clock,
  DollarSign,
  FileText,
  PlusCircle,
  Receipt,
  Settings,
  ShieldCheck,
  Users
} from 'lucide-react';
import { formatCurrency } from '../utils/currencyUtils';

const EMPLOYMENT_PRESETS = {
  fullTime: {
    label: 'Full-time employee',
    description: 'Permanent or fixed-term staff on a standard payroll cycle.',
    payBasis: 'monthly',
    payFrequency: 'monthly',
    hoursPerWeek: 45,
    paye: true,
    uifEmployee: true,
    uifEmployer: true,
    sdl: true,
    leave: 'Statutory leave accrual',
    note: 'Best for salaried staff with leave tracking and normal payroll deductions.'
  },
  partTime: {
    label: 'Part-time employee',
    description: 'Employees working reduced hours with prorated earnings and leave.',
    payBasis: 'hourly',
    payFrequency: 'monthly',
    hoursPerWeek: 24,
    paye: true,
    uifEmployee: true,
    uifEmployer: true,
    sdl: true,
    leave: 'Prorated leave accrual',
    note: 'Useful for retail, admin, and support teams with variable hours.'
  },
  contractor: {
    label: 'Independent contractor',
    description: 'External service providers paid against time, milestones, or invoices.',
    payBasis: 'invoice',
    payFrequency: 'adHoc',
    hoursPerWeek: 0,
    paye: false,
    uifEmployee: false,
    uifEmployer: false,
    sdl: false,
    leave: 'No leave accrual',
    note: 'Default as non-payroll labour unless your legal setup requires withholding.'
  },
  temporary: {
    label: 'Temporary or casual',
    description: 'Short-term workers, seasonal labour, or project-based support.',
    payBasis: 'hourly',
    payFrequency: 'weekly',
    hoursPerWeek: 30,
    paye: true,
    uifEmployee: true,
    uifEmployer: true,
    sdl: false,
    leave: 'Rules depend on duration and hours worked',
    note: 'Use when staff rotate in and out of payroll during peak periods.'
  }
};

const SA_COMPLIANCE_ITEMS = [
  {
    title: 'PAYE',
    detail: 'Track taxable earnings, deductions, and employee tax status for each pay run.'
  },
  {
    title: 'UIF',
    detail: 'Apply employee and employer UIF where the worker relationship qualifies.'
  },
  {
    title: 'SDL',
    detail: 'Flag employer Skills Development Levy liability at business level.'
  },
  {
    title: 'IRP5 / IT3(a)',
    detail: 'Prepare year-end reporting outputs and keep earning codes consistent.'
  }
];

const PayrollPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMainPage = location.pathname === '/payroll' || location.pathname === '/payroll/';

  const [employmentType, setEmploymentType] = useState('fullTime');
  const [config, setConfig] = useState({
    cycle: 'monthly',
    payDayRule: 'last-business-day',
    includePaye: true,
    includeUifEmployee: true,
    includeUifEmployer: true,
    includeSdl: true,
    includeLeave: true,
    annualSalary: 360000,
    hourlyRate: 160,
    hoursWorked: 96,
    contractorAmount: 18500
  });

  const preset = EMPLOYMENT_PRESETS[employmentType];
  const compensationMode =
    employmentType === 'fullTime'
      ? 'salary'
      : employmentType === 'contractor'
        ? 'contract'
        : 'hourly';

  const estimatedGross =
    compensationMode === 'salary'
      ? config.annualSalary / 12
      : compensationMode === 'hourly'
        ? config.hourlyRate * config.hoursWorked
        : config.contractorAmount;

  const contributions = [
    { label: 'PAYE tracking', enabled: config.includePaye && preset.paye },
    { label: 'Employee UIF', enabled: config.includeUifEmployee && preset.uifEmployee },
    { label: 'Employer UIF', enabled: config.includeUifEmployer && preset.uifEmployer },
    { label: 'SDL review', enabled: config.includeSdl && preset.sdl },
    { label: 'Leave accrual', enabled: config.includeLeave && preset.leave !== 'No leave accrual' }
  ];

  const handleConfigChange = (field, value) => {
    setConfig((current) => ({
      ...current,
      [field]: value
    }));
  };

  const applyPreset = (presetKey) => {
    const selectedPreset = EMPLOYMENT_PRESETS[presetKey];
    setEmploymentType(presetKey);
    setConfig((current) => ({
      ...current,
      cycle: selectedPreset.payFrequency === 'adHoc' ? 'ad-hoc' : selectedPreset.payFrequency,
      includePaye: selectedPreset.paye,
      includeUifEmployee: selectedPreset.uifEmployee,
      includeUifEmployer: selectedPreset.uifEmployer,
      includeSdl: selectedPreset.sdl,
      includeLeave: selectedPreset.leave !== 'No leave accrual'
    }));
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Payroll</h1>
            <p className="text-sm text-gray-500 mt-1">
              Configure South African payroll for full-time employees, part-time staff, temporary labour, and independent contractors.
            </p>
            <p className="text-sm text-[#190192] mt-2">
              SA focus: PAYE, UIF, SDL, leave treatment, and worker classification defaults.
            </p>
          </div>

          {isMainPage && (
            <button
              onClick={() => navigate('/payroll/runs/new')}
              className="px-4 py-2 bg-[#190192] text-white rounded-lg hover:bg-[#2C0DB5] font-medium transition-all flex items-center"
            >
              <PlusCircle size={18} className="mr-2" />
              New Pay Run
            </button>
          )}
        </div>
      </div>

      <div className="px-6 border-b border-gray-200 bg-white">
        <div className="flex space-x-4 overflow-x-auto">
          <button
            onClick={() => navigate('/payroll')}
            className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              location.pathname === '/payroll' || location.pathname === '/payroll/'
                ? 'border-[#190192] text-[#190192]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <DollarSign size={16} className="inline mr-1" />
            Dashboard
          </button>
          <button
            onClick={() => navigate('/payroll/runs')}
            className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              location.pathname.includes('/payroll/runs')
                ? 'border-[#190192] text-[#190192]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Calendar size={16} className="inline mr-1" />
            Pay Runs
          </button>
          <button
            onClick={() => navigate('/payroll/employees')}
            className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              location.pathname.includes('/payroll/employees')
                ? 'border-[#190192] text-[#190192]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Users size={16} className="inline mr-1" />
            Workers
          </button>
          <button
            onClick={() => navigate('/payroll/payslips')}
            className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              location.pathname.includes('/payroll/payslips')
                ? 'border-[#190192] text-[#190192]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FileText size={16} className="inline mr-1" />
            Payslips
          </button>
          <button
            onClick={() => navigate('/payroll/reports')}
            className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              location.pathname.includes('/payroll/reports')
                ? 'border-[#190192] text-[#190192]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Activity size={16} className="inline mr-1" />
            Reports
          </button>
          <button
            onClick={() => navigate('/payroll/timesheets')}
            className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              location.pathname.includes('/payroll/timesheets')
                ? 'border-[#190192] text-[#190192]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Clock size={16} className="inline mr-1" />
            Timesheets
          </button>
          <button
            onClick={() => navigate('/payroll/settings')}
            className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              location.pathname.includes('/payroll/settings')
                ? 'border-[#190192] text-[#190192]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Settings size={16} className="inline mr-1" />
            Settings
          </button>
        </div>
      </div>

      {isMainPage && (
        <div className="grid gap-6 xl:grid-cols-[1.3fr_0.9fr]">
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Payroll workflow</h2>
                  <p className="mt-1 text-sm text-gray-500">Jump directly into the pay run and payslip flow from the payroll hub.</p>
                </div>
              </div>

              <div className="mt-5 grid gap-4 md:grid-cols-3">
                <button
                  type="button"
                  onClick={() => navigate('/payroll/runs')}
                  className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-left hover:border-gray-300 hover:bg-white"
                >
                  <p className="font-medium text-gray-900">Pay Runs</p>
                  <p className="mt-1 text-sm text-gray-500">Create, review, and finalize payroll runs.</p>
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/payroll/payslips')}
                  className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-left hover:border-gray-300 hover:bg-white"
                >
                  <p className="font-medium text-gray-900">Payslips</p>
                  <p className="mt-1 text-sm text-gray-500">Review generated payslips and mark them as sent.</p>
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/payroll/settings')}
                  className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-left hover:border-gray-300 hover:bg-white"
                >
                  <p className="font-medium text-gray-900">Payroll Settings</p>
                  <p className="mt-1 text-sm text-gray-500">Maintain deduction defaults and payroll configuration.</p>
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="rounded-lg border border-gray-200 p-4">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Default worker type</p>
                  <p className="mt-2 text-lg font-semibold text-gray-900">{preset.label}</p>
                  <p className="mt-1 text-sm text-gray-500">{preset.description}</p>
                </div>
                <div className="rounded-lg border border-gray-200 p-4">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Estimated gross pay</p>
                  <p className="mt-2 text-lg font-semibold text-gray-900">{formatCurrency(estimatedGross, 'ZAR')}</p>
                  <p className="mt-1 text-sm text-gray-500">Preview only. Confirm live SARS and UIF rates before processing payroll.</p>
                </div>
                <div className="rounded-lg border border-gray-200 p-4">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Payroll posture</p>
                  <p className="mt-2 text-lg font-semibold text-gray-900 capitalize">{config.cycle}</p>
                  <p className="mt-1 text-sm text-gray-500">
                    {contributions.filter((item) => item.enabled).length} compliance controls active for this profile.
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Employment presets</h2>
                  <p className="mt-1 text-sm text-gray-500">Choose the worker profile that matches the legal and operational setup.</p>
                </div>
                <div className="rounded-full bg-purple-50 px-3 py-1 text-xs font-medium text-[#190192]">
                  ZAR / SA ruleset
                </div>
              </div>

              <div className="mt-5 grid gap-4 md:grid-cols-2">
                {Object.entries(EMPLOYMENT_PRESETS).map(([key, item]) => {
                  const isSelected = employmentType === key;

                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => applyPreset(key)}
                      className={`rounded-xl border p-4 text-left transition ${
                        isSelected
                          ? 'border-[#190192] bg-purple-50 shadow-sm'
                          : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-white'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-gray-900">{item.label}</p>
                          <p className="mt-1 text-sm text-gray-600">{item.description}</p>
                        </div>
                        {isSelected && <CheckCircle2 size={18} className="mt-1 text-[#190192]" />}
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2 text-xs">
                        <span className="rounded-full bg-white px-2.5 py-1 text-gray-700 border border-gray-200">{item.payBasis}</span>
                        <span className="rounded-full bg-white px-2.5 py-1 text-gray-700 border border-gray-200">{item.payFrequency}</span>
                        <span className="rounded-full bg-white px-2.5 py-1 text-gray-700 border border-gray-200">{item.leave}</span>
                      </div>
                      <p className="mt-4 text-sm text-gray-500">{item.note}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-purple-50 p-2 text-[#190192]">
                  <Briefcase size={18} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Payroll configuration preview</h2>
                  <p className="text-sm text-gray-500">Model the default setup before pushing staff into pay runs.</p>
                </div>
              </div>

              <div className="mt-6 grid gap-5 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Pay cycle</label>
                  <select
                    value={config.cycle}
                    onChange={(event) => handleConfigChange('cycle', event.target.value)}
                    className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#190192] focus:outline-none focus:ring-2 focus:ring-[#190192]/20"
                  >
                    <option value="weekly">Weekly</option>
                    <option value="fortnightly">Fortnightly</option>
                    <option value="monthly">Monthly</option>
                    <option value="ad-hoc">Ad hoc</option>
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Pay date rule</label>
                  <select
                    value={config.payDayRule}
                    onChange={(event) => handleConfigChange('payDayRule', event.target.value)}
                    className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#190192] focus:outline-none focus:ring-2 focus:ring-[#190192]/20"
                  >
                    <option value="last-business-day">Last business day</option>
                    <option value="fixed-day">Fixed calendar day</option>
                    <option value="next-business-day">Next business day</option>
                    <option value="on-approval">On approval</option>
                  </select>
                </div>

                {compensationMode === 'salary' && (
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Annual salary</label>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      value={config.annualSalary}
                      onChange={(event) => handleConfigChange('annualSalary', Number(event.target.value))}
                      className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#190192] focus:outline-none focus:ring-2 focus:ring-[#190192]/20"
                    />
                  </div>
                )}

                {compensationMode === 'hourly' && (
                  <>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">Hourly rate</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={config.hourlyRate}
                        onChange={(event) => handleConfigChange('hourlyRate', Number(event.target.value))}
                        className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#190192] focus:outline-none focus:ring-2 focus:ring-[#190192]/20"
                      />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">Hours in pay period</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={config.hoursWorked}
                        onChange={(event) => handleConfigChange('hoursWorked', Number(event.target.value))}
                        className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#190192] focus:outline-none focus:ring-2 focus:ring-[#190192]/20"
                      />
                    </div>
                  </>
                )}

                {compensationMode === 'contract' && (
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Contract or invoice amount</label>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      value={config.contractorAmount}
                      onChange={(event) => handleConfigChange('contractorAmount', Number(event.target.value))}
                      className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#190192] focus:outline-none focus:ring-2 focus:ring-[#190192]/20"
                    />
                  </div>
                )}
              </div>

              <div className="mt-6 grid gap-3 md:grid-cols-2">
                {[
                  ['includePaye', 'Track PAYE'],
                  ['includeUifEmployee', 'Deduct employee UIF'],
                  ['includeUifEmployer', 'Accrue employer UIF'],
                  ['includeSdl', 'Review SDL applicability'],
                  ['includeLeave', 'Accrue leave automatically']
                ].map(([field, label]) => (
                  <label key={field} className="flex items-center gap-3 rounded-lg border border-gray-200 px-4 py-3 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={config[field]}
                      onChange={(event) => handleConfigChange(field, event.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-[#190192] focus:ring-[#190192]"
                    />
                    {label}
                  </label>
                ))}
              </div>

              <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-gray-500">Preview</p>
                <div className="mt-3 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                  <div>
                    <p className="text-3xl font-semibold text-gray-900">{formatCurrency(estimatedGross, 'ZAR')}</p>
                    <p className="mt-1 text-sm text-gray-500">Estimated gross pay for the selected worker type.</p>
                  </div>
                  <div className="grid gap-2 text-sm text-gray-600">
                    {contributions.map((item) => (
                      <div key={item.label} className="flex items-center gap-2">
                        <span className={`h-2.5 w-2.5 rounded-full ${item.enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
                        {item.label}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-purple-50 p-2 text-[#190192]">
                  <ShieldCheck size={18} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">South Africa compliance anchors</h2>
                  <p className="text-sm text-gray-500">Core items to configure per worker and at business level.</p>
                </div>
              </div>

              <div className="mt-5 space-y-3">
                {SA_COMPLIANCE_ITEMS.map((item) => (
                  <div key={item.title} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <div className="flex items-center gap-2">
                      <Receipt size={16} className="text-[#190192]" />
                      <p className="font-medium text-gray-900">{item.title}</p>
                    </div>
                    <p className="mt-2 text-sm text-gray-600">{item.detail}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900">How the selected profile behaves</h2>
              <div className="mt-5 space-y-4 text-sm">
                <div className="rounded-lg bg-gray-50 p-4">
                  <p className="text-gray-500">Pay basis</p>
                  <p className="mt-1 font-medium text-gray-900">{preset.payBasis}</p>
                </div>
                <div className="rounded-lg bg-gray-50 p-4">
                  <p className="text-gray-500">Expected hours</p>
                  <p className="mt-1 font-medium text-gray-900">
                    {preset.hoursPerWeek > 0 ? `${preset.hoursPerWeek} hours / week` : 'Driven by contract or invoice'}
                  </p>
                </div>
                <div className="rounded-lg bg-gray-50 p-4">
                  <p className="text-gray-500">Leave treatment</p>
                  <p className="mt-1 font-medium text-gray-900">{preset.leave}</p>
                </div>
                <div className="rounded-lg bg-amber-50 p-4 text-amber-900 border border-amber-200">
                  <p className="font-medium">Implementation note</p>
                  <p className="mt-1">
                    Contractors are shown separately from employees so you can avoid applying UIF, SDL, or leave rules by default where the engagement is genuinely independent.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Outlet />
    </div>
  );
};

export default PayrollPage;
